// Backtest cards (docs/040 §9). acp.rs attaches a `backtest_run` block to the
// completed tool update for every run made in the turn; the card is data, so
// the same record can be drawn by the web app later (docs/005 §5a).

export type BacktestCardRef = {
  runId: string;
  name: string;
  status: string;
  period: [string | null, string | null];
  totalReturn: number | null;
  maxDrawdown: number | null;
  trades: number | null;
  winRate: number | null;
  benchmark: { symbol?: string; total_return?: number; max_drawdown?: number } | null;
  finalCheck: boolean;
  variantIndex: number | null;
  strategyId: string | null;
  strategyName: string | null;
  version: number | null;
};

export function readBacktestCard(rec: Record<string, unknown>): BacktestCardRef | undefined {
  if (typeof rec.runId !== "string" || !rec.runId) return undefined;
  const num = (v: unknown) => (typeof v === "number" && isFinite(v) ? v : null);
  const period = Array.isArray(rec.period) ? rec.period : [];
  return {
    runId: rec.runId,
    name: typeof rec.name === "string" ? rec.name : "",
    status: typeof rec.status === "string" ? rec.status : "",
    period: [(period[0] as string) ?? null, (period[1] as string) ?? null],
    totalReturn: num(rec.totalReturn),
    maxDrawdown: num(rec.maxDrawdown),
    trades: num(rec.trades),
    winRate: num(rec.winRate),
    benchmark: rec.benchmark && typeof rec.benchmark === "object" ? (rec.benchmark as any) : null,
    finalCheck: rec.finalCheck === true,
    variantIndex: num(rec.variantIndex),
    strategyId: typeof rec.strategyId === "string" ? rec.strategyId : null,
    strategyName: typeof rec.strategyName === "string" ? rec.strategyName : null,
    version: num(rec.version),
  };
}

export function collectBacktestCards(node: unknown, out: BacktestCardRef[] = []): BacktestCardRef[] {
  if (!node || typeof node !== "object") return out;
  if (Array.isArray(node)) {
    node.forEach((n) => collectBacktestCards(n, out));
    return out;
  }
  const rec = node as Record<string, unknown>;
  if (rec.type === "backtest_run") {
    const c = readBacktestCard(rec);
    if (c) out.push(c);
    return out;
  }
  Object.values(rec).forEach((v) => collectBacktestCards(v, out));
  return out;
}

/** A share (win rate, exposure): no sign. */
export function share(v: number | null | undefined, digits = 0): string {
  if (v == null || !isFinite(v)) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}

export function pct(v: number | null | undefined, digits = 1): string {
  if (v == null || !isFinite(v)) return "—";
  const s = (v * 100).toFixed(digits);
  return `${v > 0 ? "+" : ""}${s}%`;
}

// Paper account cards (docs/041 §5): one per deployment, attached to the turn
// that deployed it. The stored copy is a snapshot; the card refreshes from the
// app (deployment_detail) and on scheduler updates.
export type PaperCardRef = {
  deploymentId: string;
  strategyName: string | null;
  version: number | null;
  status: string;
  startDate: string | null;
  lastEvalDay: string | null;
  returnSinceStart: number | null;
  benchmark: string | null;
  benchmarkReturn: number | null;
  portfolioValue: number | null;
  lastSignal: { signal_date?: string; side?: string; shares?: number; symbol?: string } | null;
};

export function readPaperCard(rec: Record<string, unknown>): PaperCardRef | undefined {
  if (typeof rec.deploymentId !== "string" || !rec.deploymentId) return undefined;
  const num = (v: unknown) => (typeof v === "number" && isFinite(v) ? v : null);
  const str = (v: unknown) => (typeof v === "string" ? v : null);
  return {
    deploymentId: rec.deploymentId,
    strategyName: str(rec.strategyName),
    version: num(rec.version),
    status: str(rec.status) ?? "",
    startDate: str(rec.startDate),
    lastEvalDay: str(rec.lastEvalDay),
    returnSinceStart: num(rec.returnSinceStart),
    benchmark: str(rec.benchmark),
    benchmarkReturn: num(rec.benchmarkReturn),
    portfolioValue: num(rec.portfolioValue),
    lastSignal: rec.lastSignal && typeof rec.lastSignal === "object" ? (rec.lastSignal as any) : null,
  };
}

export function collectPaperCards(node: unknown, out: PaperCardRef[] = []): PaperCardRef[] {
  if (!node || typeof node !== "object") return out;
  if (Array.isArray(node)) {
    node.forEach((n) => collectPaperCards(n, out));
    return out;
  }
  const rec = node as Record<string, unknown>;
  if (rec.type === "paper_account") {
    const c = readPaperCard(rec);
    if (c) out.push(c);
    return out;
  }
  Object.values(rec).forEach((v) => collectPaperCards(v, out));
  return out;
}

// Alerts and watchlists (docs/041 §5.3, unified alerts). Made only by the agent; acp.rs
// attaches an `alert` / `watchlist` block to the tool update that created one.

export type AlertCardRef = {
  alertId: string;
  name: string;
  route: "symbolic" | "agent" | string;
  status: string;
  schedule: string;
  covers: number;
  watchlist: string | null;
  lastRunAt: number | null;
  lastFiredAt: number | null;
  email: boolean;
  whatsapp: boolean;
};

export function readAlertCard(rec: Record<string, unknown>): AlertCardRef | undefined {
  if (typeof rec.alertId !== "string" || !rec.alertId) return undefined;
  const num = (v: unknown) => (typeof v === "number" && isFinite(v) ? v : null);
  return {
    alertId: rec.alertId,
    name: typeof rec.name === "string" ? rec.name : "Alert",
    route: typeof rec.route === "string" ? rec.route : "",
    status: typeof rec.status === "string" ? rec.status : "",
    schedule: typeof rec.schedule === "string" ? rec.schedule : "",
    covers: num(rec.covers) ?? 0,
    watchlist: typeof rec.watchlist === "string" ? rec.watchlist : null,
    lastRunAt: num(rec.lastRunAt),
    lastFiredAt: num(rec.lastFiredAt),
    email: rec.email === true,
    whatsapp: rec.whatsapp === true,
  };
}

export type WatchlistCardRef = { watchlistId: string; name: string; count: number; symbols: string[] };

export function readWatchlistCard(rec: Record<string, unknown>): WatchlistCardRef | undefined {
  if (typeof rec.watchlistId !== "string" || !rec.watchlistId) return undefined;
  return {
    watchlistId: rec.watchlistId,
    name: typeof rec.name === "string" ? rec.name : "Watchlist",
    count: typeof rec.count === "number" ? rec.count : 0,
    symbols: Array.isArray(rec.symbols) ? rec.symbols.filter((x): x is string => typeof x === "string") : [],
  };
}

function collectByType<T>(type: string, read: (r: Record<string, unknown>) => T | undefined, node: unknown, out: T[]): T[] {
  if (!node || typeof node !== "object") return out;
  if (Array.isArray(node)) {
    node.forEach((n) => collectByType(type, read, n, out));
    return out;
  }
  const rec = node as Record<string, unknown>;
  if (rec.type === type) {
    const c = read(rec);
    if (c) out.push(c);
    return out;
  }
  Object.values(rec).forEach((v) => collectByType(type, read, v, out));
  return out;
}

export const collectAlertCards = (node: unknown) => collectByType("alert", readAlertCard, node, [] as AlertCardRef[]);
export const collectWatchlistCards = (node: unknown) => collectByType("watchlist", readWatchlistCard, node, [] as WatchlistCardRef[]);

/** "3 min ago", "2 h ago", "Oct 21" for a unix time (seconds). */
export function ago(ts: number | null | undefined): string {
  if (!ts) return "never";
  const s = Date.now() / 1000 - ts;
  if (s < 90) return "just now";
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  if (s < 86400 * 2) return `${Math.round(s / 3600)} h ago`;
  return new Date(ts * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
